import { useMemo, useState } from "react";
import {
  ToggleOption_CheckoutConfigOptionGroupFragment,
  ToggleOption_CheckoutConfigPotentialOptionFragment,
} from "src/fragments/circularFragments.generated";
import { ConfiguratorStepper, ImageDisplay, ModalWrapper, StepWrapper } from "src/pages/configurator/components";
import { ImageDisplayImageProps } from "src/pages/configurator/components/ImageDisplay";
import {
  ComparePackageOptionsContent,
  ExpiredOptionsSwitch,
  NoOptions,
  OptionListContainer,
} from "src/pages/configurator/components/options";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { ProductAddOnCard } from "~components";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { fail, useComputed, useTestIds } from "~utils";
import { UserWarning } from "../components/UserWarning";
import { ADDL_PERSONALIZATION_IMAGE_DISPLAY, getCutoffDaysRemaining, optionGroupFilter } from "../Configurator.utils";
import { InteriorStep_CheckoutOptionGroupFragment } from "./Interior.step.generated";

export function OptionsStep() {
  const { toggleOption, checkoutConfig, formState, showPriceLoadingState, autoSaving, selectedOptionIds } =
    useConfiguratorContext();
  const tid = useTestIds({}, "options-step");
  const [showExpiredOptions, setShowExpiredOptions] = useState(false);
  const [lastToggledOptionId, setLastToggledOptionId] = useState<string | null>(null);
  const [compareCCOG, setCompareCCOG] = useState<InteriorStep_CheckoutOptionGroupFragment | undefined>(undefined);

  const { addOnOptions, filteredAddOnOptions } = useMemo(() => {
    const optionsByStep = checkoutConfig?.optionGroups.groupBy((ccog) => ccog.step);
    // Only show options that do not have a parent.
    const addOnOptions =
      optionsByStep?.[CheckoutConfigOptionGroupStep.OptionsAddon]?.filter((ccog) => !ccog.parentGroup) ?? [];

    return {
      addOnOptions,
      filteredAddOnOptions: addOnOptions.filter(optionGroupFilter(showExpiredOptions, selectedOptionIds)),
    };
  }, [checkoutConfig, showExpiredOptions, selectedOptionIds]);

  // Get option image for left <ImageDisplay>
  const optionImages: ImageDisplayImageProps[] = useMemo(() => {
    const filteredOptions = filteredAddOnOptions;
    // Attempt to display the last toggled option image if the user has interacted with this step
    if (lastToggledOptionId) {
      const lastToggledOptionImage = filteredOptions
        .find((o) => o.options.some((o) => o.id === lastToggledOptionId))
        ?.options.find((o) => o.id === lastToggledOptionId)?.option.optionImage?.largePreview;

      if (!lastToggledOptionImage) return ADDL_PERSONALIZATION_IMAGE_DISPLAY;

      return [{ id: lastToggledOptionId, kind: "image", imageUrl: lastToggledOptionImage }];
    }

    // Otherwise use the first option image, finally falling back to the placeholder
    const firstOption = filteredOptions.first?.options.first?.option;
    if (!firstOption?.optionImage) return ADDL_PERSONALIZATION_IMAGE_DISPLAY;

    return [{ kind: "image", id: firstOption.id, imageUrl: firstOption.optionImage?.largePreview }];
  }, [lastToggledOptionId, filteredAddOnOptions]);

  // TODO: as util
  const someOptionsHaveExpired = addOnOptions.some(
    (ccog) => ccog.cutoffDate && getCutoffDaysRemaining(ccog.cutoffDate) <= 0,
  );

  /**
   *
   * Handle Interactive Floor Plans
   *
   * 1) Initial floor plan determined based on sort order of images when entering step regardless of what (if any) options are chosen
   *  - Note: we could instead show the floor plan that has a selected option (if at least one is chosen) if desired
   * 2) Upon an option selection, highlight the floor plan(s) that has the selected option
   *  - if the selected option isn't currently displayed on the floor plan, change the floor plan to show
   *    the next floor plan that has the selected option
   * 3) Upon de-selection of an option, remove the highlight the floor plan(s) that has the de-selected option
   *  - if the de-selected option isn't currently displayed on the floor plan, change the floor plan to show
   *    the next floor plan that has the de-selected option
   */

  function handleOptionClick(
    ccog: ToggleOption_CheckoutConfigOptionGroupFragment,
    cco: ToggleOption_CheckoutConfigPotentialOptionFragment,
  ) {
    toggleOption(ccog, cco);
    setLastToggledOptionId(cco.id);
  }

  const isDisabled = !checkoutConfig?.canEdit.allowed;
  const loading = useComputed(() => formState.loading, [formState]);

  return (
    <>
      <StepWrapper
        hideBorder
        hideTopOnMobile
        loading={loading}
        loadingView="personalization"
        title="Additional Options"
        description="Further enhance your home by adding bonus features."
        warningNode={
          !loading &&
          isDisabled && (
            <UserWarning title="Add-ons can no longer be modified." disabledReasons={[checkoutConfig?.canEdit]} />
          )
        }
        left={<ImageDisplay images={optionImages} {...tid.imageDisplay} />}
        right={
          <>
            {someOptionsHaveExpired && (
              <ExpiredOptionsSwitch
                showExpiredOptions={showExpiredOptions}
                setShowExpiredOptions={setShowExpiredOptions}
              />
            )}
            <OptionListContainer>
              {filteredAddOnOptions.isEmpty && <NoOptions showExpiredOptions={showExpiredOptions} />}
              {filteredAddOnOptions.map((ccog) => {
                // Find the selected option ID for this group
                const selectedOptionId = ccog.options.find((option) => selectedOptionIds.includes(option.option.id))
                  ?.option.id;
                return (
                  <ProductAddOnCard
                    key={ccog.id}
                    autoSaving={showPriceLoadingState(selectedOptionId)}
                    ccfgOptionGroup={ccog}
                    selectedOptionIds={selectedOptionIds}
                    onSelect={handleOptionClick}
                    {...tid.productAddOnCard}
                    allOptionGroups={checkoutConfig?.optionGroups ?? []}
                    disabled={isDisabled || autoSaving}
                    onCompare={(maybeChildCCOG: InteriorStep_CheckoutOptionGroupFragment | undefined) =>
                      setCompareCCOG(maybeChildCCOG ?? ccog)
                    }
                  />
                );
              })}
            </OptionListContainer>
          </>
        }
      />
      <ConfiguratorStepper />
      <ModalWrapper isOpen={!!compareCCOG} title={compareCCOG?.group.name} close={() => setCompareCCOG(undefined)}>
        {compareCCOG && checkoutConfig && (
          <ComparePackageOptionsContent
            checkoutConfigId={checkoutConfig.id}
            rpogId={compareCCOG.group.id}
            ccog={compareCCOG}
            selectedOptionIds={selectedOptionIds}
            onSelect={(selectedOptionId) =>
              toggleOption(
                compareCCOG,
                compareCCOG.options.find((cco) => cco.id === selectedOptionId) ?? fail(`Missing ${selectedOptionId}`),
              )
            }
          />
        )}
      </ModalWrapper>
    </>
  );
}

// example output:
// {
//  "Ground Level": ["DECKSTR001', 'UNDGRDGEN1'", ...] },
//  "First Floor": ["DECKSTR001', 'SUNROOM001'", ...] },
// }
